import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RoutePaths } from '@global/routePaths';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { catchError, first, map, switchMap, take, tap } from 'rxjs/operators';
import { InitFlowService } from '@core/services/api/http/initFlow.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { getInitFlow, UpdateInitFlow } from '@store/flow-data/init-flow';
import { getQuote } from '@store/flow-data/quote';
import { Quote } from '@core/models/swagger';
import { REFERRAL_CODE_COOPERATION_ID, REFERRAL_CODE_ID, REFERRAL_CODE_ID_URL, REFERRAL_CODE_MARKETING_ID } from '@global/constants';

@Injectable({
  providedIn: 'root',
})
export class NavigationGuard implements CanActivate {
  environment = environment;

  private readonly DESKTOP = 'desktop';
  private readonly TABLET = 'tablet';
  private readonly MOBILE = 'mobile';

  constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly initFlowService: InitFlowService,
    private readonly deviceDetectorService: DeviceDetectorService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const extraState = this.router.getCurrentNavigation()?.extras?.state;
    const isPre = this.environment.environmentName === 'SND';
    const isDevPreview = !this.environment.production && state.url === '/components-preview';

    // Block by environment
    if (!isPre && !extraState?.allowNavigation) {
      this.redirectToError(route.queryParams, extraState);
      return of(false);
    }

    // Preview or Bypass
    if (isDevPreview || extraState?.skipGuard) {
      return of(true);
    }

    return combineLatest([this.store.select(getInitFlow), this.store.select(getQuote)]).pipe(
      first(),
      switchMap(([initFlowData, quote]) => {
        const campaignCode = route.queryParams?.campaignCode;
        const promotionCode = route.queryParams?.promotionCode;

        const hasValidInitFlow =
          initFlowData &&
          (initFlowData?.campaignInfo?.campaignCode === campaignCode ||
            (!campaignCode && initFlowData?.campaignInfo?.campaignCode === 'defaultCampaignCode')) &&
          initFlowData?.promotionInfo?.promotionCode === promotionCode;

        if (hasValidInitFlow) return of(true);

        let queryParamsChanged: boolean = false;

        if (initFlowData?.promotionInfo?.promotionCode !== promotionCode) {
          queryParamsChanged = true;
        }

        return this.handleMissingInitFlow({
          campaignCode,
          promotionCode,
          quote,
          route,
          extraState,
          queryParamsChanged,
        });
      })
    );
  }

  private handleMissingInitFlow({
    campaignCode,
    promotionCode,
    referralCode,
    quote,
    route,
    extraState,
    queryParamsChanged,
  }: {
    campaignCode?: string;
    promotionCode?: string;
    referralCode?: string;
    quote: Quote;
    route: ActivatedRouteSnapshot;
    extraState: any;
    queryParamsChanged: boolean;
  }): Observable<boolean> {
    let queryParams = { ...route.queryParams };

    return this.initFlowService.postInitFlow({ campaignCode, promotionCode }).pipe(
      first(),
      tap(initFlowData => {
        // const hasReferralCode =
        //   referralCode ||
        //   quote?.externalReferences.some(ref => [REFERRAL_CODE_ID, REFERRAL_CODE_ID_URL].includes(ref.key));

        // if (hasReferralCode) {
        //   initFlowData.campaignInfo = {
        //     ...initFlowData.campaignInfo,
        //     cooperationId: REFERRAL_CODE_COOPERATION_ID,
        //     marketingId: REFERRAL_CODE_MARKETING_ID,
        //   };
        //   queryParams.referralCode = hasReferralCode;
        //   queryParamsChanged = true;
        // }

        if (campaignCode && campaignCode !== initFlowData?.campaignInfo?.campaignCode) {
          queryParams.campaignCode = initFlowData?.campaignInfo?.campaignCode;
          queryParamsChanged = true;
        }

        if (promotionCode && promotionCode !== initFlowData?.promotionInfo?.promotionCode) {
          queryParams.promotionCode = initFlowData?.promotionInfo?.promotionCode;
          queryParamsChanged = true;
        }

        if (promotionCode && promotionCode !== initFlowData?.promotionInfo?.promotionCode) {
          queryParams.promotionCode = initFlowData?.promotionInfo?.promotionCode;
          queryParamsChanged = true;
        }

        this.store.dispatch(UpdateInitFlow({ data: initFlowData }));
      }),
      switchMap(_ => {
        let routePath: string = RoutePaths.NEW_QUOTATION.SITUATION.VERTI_CLIENT.route;

        if (!queryParamsChanged && quote?.isVertiClient !== undefined) {
          return of(true);
        }

        this.router.navigate([routePath], {
          queryParamsHandling: 'merge',
          queryParams,
          state: { ...extraState, skipGuard: true },
        });
        return of(false);
      }),
      catchError(() => {
        this.router.navigate([RoutePaths.GENERIC_ERROR.route]);
        return EMPTY;
      })
    );
  }

  private redirectToError(queryParams: Params, state: any): void {
    this.router.navigate([RoutePaths.GENERIC_ERROR.route], {
      queryParamsHandling: 'merge',
      queryParams,
      state: { ...state, skipGuard: true },
    });
  }

  private getDeviceType(): string {
    if (this.deviceDetectorService.isDesktop()) {
      return this.DESKTOP;
    }
    if (this.deviceDetectorService.isTablet()) {
      return this.TABLET;
    }
    if (this.deviceDetectorService.isMobile()) {
      return this.MOBILE;
    }
  }
}
